import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Body2 } from "@activecollab/components";
import PropTypes from "prop-types";
import BuyNowButton from "~/components/BuyNowButton/BuyNowButton";
import BalanceTooltipContent from "~/components/Subscription/SubscriptionPlanDetails/BalanceTooltipContent";
import PriceTooltipContent from "~/components/Subscription/SubscriptionPlanDetails/PriceTooltipContent";
import StorageUsage from "~/components/Subscription/SubscriptionPlanDetails/StorageUsage";
import SubscriptionInfoIconWithTooltip from "~/components/Subscription/SubscriptionPlanDetails/SubscriptionInfoIconWithTooltip";
import {
  getAddOnsAsString,
  getCurrentPlanUsersLimitation,
  getCurrentPlanVerboseName,
  getCurrentUsersCount,
} from "~/store/selectors/selector.accountSettings";
import { getPriceBreakdown } from "~/store/selectors/selector.subscriptionBrakeDownPrice";
import { roundAmount } from "~/utils/utils";

const LimitedPlanDetails = ({ planInfo, storageUsage, balanceTotal, isCurrentPlanFreePlan }) => {
  const { t } = useTranslation();
  const verboseName = useSelector((state) => getCurrentPlanVerboseName(state));
  const addOnNames = useSelector((state) => getAddOnsAsString(state));
  const currentMembers = useSelector((state) => getCurrentUsersCount(state));
  const maxMembers = useSelector((state) => getCurrentPlanUsersLimitation(state));
  const priceBreakDown = useSelector((state) => getPriceBreakdown(state));

  return (
    <>
      {isCurrentPlanFreePlan ? (
        <BuyNowButton
          url="/subscription/choose-plan"
          className="tw-absolute tw-right-0"
        />
      ) : null}
      <Body2
        color="primary"
        weight="bold"
        className="tw-mr-8"
      >
        {t("Plan")}
      </Body2>
      <Body2
        color="tertiary"
        data-testid="planLabel"
      >
        {verboseName}
      </Body2>
      <Body2
        color="primary"
        weight="bold"
        className="tw-mr-8"
      >
        {t("Price")}
      </Body2>
      <div className="tw-flex tw-items-center">
        <Body2
          color="tertiary"
          className="tw-mr-2"
          data-testid="priceLabel"
        >
          {verboseName}
        </Body2>
        {verboseName !== "Free" && priceBreakDown.total > 0 ? (
          <>
            <Body2
              color="tertiary"
              className="tw-mr-2"
              whitespace="pre-wrap"
            >
              + ${priceBreakDown.total} for Bundles
            </Body2>
            <SubscriptionInfoIconWithTooltip title={<PriceTooltipContent />} />
          </>
        ) : null}
      </div>
      {verboseName !== "Free" ? (
        <>
          <Body2
            color="primary"
            weight="bold"
            className="tw-mr-8"
            data-testid="additionalBundlesLabel"
          >
            {t("Additional Bundles")}
          </Body2>
          <div className="tw-flex">
            {addOnNames?.length ? (
              <Body2
                color="tertiary"
                className="tw-mr-2"
              >
                {addOnNames}
              </Body2>
            ) : null}
            <Body2 data-testid="manageBundlesLabel">
              <Link
                to="/subscription/manage-add-ons"
                className="activity-link"
              >
                {t("Manage Bundles")}
              </Link>
            </Body2>
          </div>
        </>
      ) : null}
      <Body2
        color="primary"
        weight="bold"
        className="tw-mr-8"
      >
        {t("Team Members")}
      </Body2>
      <Body2 color="tertiary">
        {currentMembers}/{maxMembers}
        {verboseName !== "Free" ? (
          <span>
            <Link
              to="/subscription/manage-add-ons/extra-members"
              className="tw-ml-2 activity-link"
            >
              {t("Manage Additional Members")}
            </Link>
          </span>
        ) : null}
      </Body2>
      <Body2
        color="primary"
        weight="bold"
        className="tw-mr-8"
      >
        {t("Storage Usage")}
      </Body2>
      <div className="tw-flex tw-items-center">
        <StorageUsage
          storage={`${planInfo.storage}`}
          storageUsage={storageUsage}
        />
      </div>
      {addOnNames.length && !planInfo.is_suspended && planInfo.is_activated ? (
        <>
          <Body2
            color="primary"
            weight="bold"
            className="tw-mr-8"
          >
            {planInfo?.nextBillDateLabel}
          </Body2>
          <Body2 color="tertiary">{planInfo?.nextBillDateValue}</Body2>
        </>
      ) : null}

      {Number(balanceTotal) > 0 ? (
        <>
          <Body2
            color="primary"
            weight="bold"
            className="tw-mr-8"
          >
            {t("Balance")}
          </Body2>
          <div className="tw-flex tw-items-center">
            <Body2
              color="tertiary"
              className="tw-mr-2"
            >
              ${roundAmount(balanceTotal)}
            </Body2>
            <SubscriptionInfoIconWithTooltip title={<BalanceTooltipContent />} />
          </div>
        </>
      ) : null}
    </>
  );
};

LimitedPlanDetails.propTypes = {
  planInfo: PropTypes.object,
  storageUsage: PropTypes.object,
  balanceTotal: PropTypes.number,
  isCurrentPlanFreePlan: PropTypes.bool,
};

export default LimitedPlanDetails;
